import { OrderedSet, Set, Map, List, fromJS } from 'immutable';
import sentenceCase from 'sentence-case';
import radians from 'degrees-radians';
import AdStateRecord from 'Records/AdStateRecord';
import ComponentRecord from 'Records/ComponentRecord';
import KeyframeRecord from 'Records/KeyframeRecord';

export const APP = 'app';
export const ADSTATES = 'adStates';
export const COMPONENTS = 'components';
export const SUBCOMPONENTS = 'subComponents';
export const KEYFRAMES = 'keyframes';
export const VARIANTS = 'variants';
export const KEYS = 'keys';
export const ASSETIDS = 'assetIds';
export const ASSETS = 'assets';
export const PAST = 'past';
export const PRESENT = 'present';
export const FUTURE = 'future';
export const AD = 'ad';
export const METADATA = 'metadata';
export const UI = 'ui';
export const FONTS = 'fonts';
export const VIEWPORT = 'viewport';
export const DIMENSIONS = 'dimensions';
export const SETTINGS = 'settings';
export const SELECTED = 'selected';
export const WIDTH = 'width';
export const HEIGHT = 'height';
export const KEY = 'key';

export const ADSTATES_PATH = [APP, PRESENT, AD, ADSTATES];

export const initialState = Map({
  [ADSTATES]: Map(),
  [COMPONENTS]: Map(),
  [KEYFRAMES]: Map(),
  [KEYS]: new Map({
    [ADSTATES]: 0, // Mantain key increments rather than rely on wayveconfig
    [COMPONENTS]: 0,
    [KEYFRAMES]: 0,
    [VARIANTS]: 0,
  }),
});

export function getKeyframesForComponent({ clipboardState, state, component }) {
  const sourceState = clipboardState || state;
  if (clipboardState) {
    return sourceState
      .getIn([COMPONENTS, component.key, KEYFRAMES])
      .map(keyframeId => clipboardState.get('keyframes').find(k => k.key === keyframeId));
  }
  return sourceState
    .getIn([COMPONENTS, component.key, KEYFRAMES])
    .map(keyframeId => state.getIn([KEYFRAMES, keyframeId]));
}

export function getSubComponentsForComponent({ state, component }) {
  return state
    .getIn([COMPONENTS, component.key, SUBCOMPONENTS])
    .map(componentId => state.getIn([COMPONENTS, componentId]));
}

export function setComponentKeyframeSelection({ mutable, component, selected }) {
  const keyframes = getKeyframesForComponent({ state: mutable, component });
  keyframes.forEach((k) => {
    if (k.t > -1) {
      mutable.setIn([KEYFRAMES, k.key, 'selected'], selected);
    }
  });
}

export function deselectComponent({ mutable, component }) {
  mutable.setIn([COMPONENTS, component.key, 'selected'], false);
  setComponentKeyframeSelection({
    component,
    mutable,
    selected: false,
  });
}

export function deselectComponentWithZeroSelectedKeyframes({ mutable }) {
  const selectedComponents = mutable.get(COMPONENTS).filter(c => c.selected);
  selectedComponents.filter(c =>
    getKeyframesForComponent({ state: mutable, component: c }).filter(m => m.selected).count() === 0,
  ).forEach(c => deselectComponent({ mutable, component: c }));
}

export function deselectComponents({ mutable }) {
  const selectedComponents = mutable.get(COMPONENTS).filter(c => c.selected);
  selectedComponents.forEach(component =>
    deselectComponent({
      component,
      mutable,
    }));
}

export function deselectKeyframe({ mutable, keyframe }) {
  mutable.setIn([KEYFRAMES, keyframe.key, 'selected'], false);
}

export function deselectKeyframes({ mutable }) {
  const selectedKeyframes = mutable.get(KEYFRAMES).filter(c => c.selected);
  selectedKeyframes.forEach(keyframe =>
    deselectKeyframe({
      mutable,
      keyframe,
    }));
}

export function incrementKey({ mutable, type }) {
  const nextKey = mutable.getIn([KEYS, type]) + 1;
  mutable.setIn([KEYS, type], nextKey);
  return nextKey.toString();
}


export function getActiveGroup({ mutable, adState }) {
  return adState.componentList(mutable).find(c => c.isActiveGroup());
}

export function addComponent({ mutable, adState, parent, component, keyframes }) {
  const activeGroup = parent || getActiveGroup({ mutable, adState });

  const keyframeMap = keyframes.reduce((r, k) => r.set(k.key, k), Map());

  if (activeGroup) {
    addSubComponentsToGroup({
      mutable,
      subComponents: new List([component]),
      group: activeGroup,
    });
  } else {
    const componentList = adState.components.concat(component.key);
    mutable.setIn([ADSTATES, adState.key, COMPONENTS], componentList);
  }

  mutable.setIn([COMPONENTS, component.key], component);
  mutable.mergeIn([KEYFRAMES], keyframeMap);
}

export function addAdState({ mutable, adState }) {
  mutable.setIn([ADSTATES, adState.key], adState);
}

export function removeComponentsFromAdState({ mutable, components, adState }) {
  const deletionSet = new OrderedSet(components.toList().map(m => m.key));
  const remainingSet = new OrderedSet(
    mutable.getIn([ADSTATES, adState.key, COMPONENTS]),
  ).subtract(deletionSet);

  mutable.setIn([ADSTATES, adState.key, COMPONENTS], remainingSet.toList());
}

export function deleteComponent({ mutable, adState, component, deep = true }) {
  const componentsToDelete = deep
    ? component.toList(mutable)
    : new List([component]);

  const keyframesToRemove = componentsToDelete
    .map(c => getKeyframesForComponent({ state: mutable, component: c }))
    .flatten(true);

  const activeGroup = getActiveGroup({ mutable, adState });
  if (activeGroup) {
    removeSubComponentsFromGroup({
      mutable,
      subComponents: new List([component]),
      group: activeGroup,
    });
  } else {
    removeComponentsFromAdState({
      mutable,
      components: new List([component]),
      adState,
    });
  }

  componentsToDelete.forEach(c => mutable.deleteIn([COMPONENTS, c.key]));

  keyframesToRemove.forEach(k => mutable.deleteIn([KEYFRAMES, k.key]));
}

export function deleteKeyframes({ mutable, keyframes, component }) {
  const deletionSet = Set(keyframes.map(k => k.key));
  const remainingSet = Set(component.keyframes).subtract(deletionSet);
  mutable.setIn([COMPONENTS, component.key, KEYFRAMES], remainingSet.toList());

  keyframes.sortBy(k => k.key).reverse().forEach((k) => {
    mutable.deleteIn([KEYFRAMES, k.key]);
  });
}

export function deleteAdstate({ mutable, adState }) {
  mutable.deleteIn([ADSTATES, adState.key]);
}

export function removeSubComponentsFromGroup({ mutable, subComponents, group }) {
  const deletionSet = new OrderedSet(subComponents.toList().map(m => m.key));
  const remainingSet = new OrderedSet(mutable.getIn([COMPONENTS, group.key, SUBCOMPONENTS])).subtract(deletionSet);

  mutable.setIn([COMPONENTS, group.key, SUBCOMPONENTS], remainingSet.toList());
}

export function addSubComponentsToGroup({ mutable, subComponents, group }) {
  const additionSet = new OrderedSet(subComponents.toList().map(m => m.key));
  const newSet = new OrderedSet(mutable.getIn([COMPONENTS, group.key, SUBCOMPONENTS])).concat(additionSet);

  mutable.setIn([COMPONENTS, group.key, SUBCOMPONENTS], newSet.toList());
}

export function addComponentsToAdState({ mutable, components, adState }) {
  const additionSet = new OrderedSet(components.toList().map(m => m.key));
  const newSet = new OrderedSet(mutable.getIn([ADSTATES, adState.key, COMPONENTS])).concat(additionSet);

  mutable.setIn([ADSTATES, adState.key, COMPONENTS], newSet.toList());
}

export function combineStyle(baseStyle, newStyle) {
  return baseStyle
    ? Object.assign(baseStyle.toJS(), newStyle)
    : newStyle;
}

export function addKeyframe({ mutable, component, style, actions, t }) {
  const keyframe = new KeyframeRecord({
    key: incrementKey({ mutable, type: KEYFRAMES }),
    t,
    style: Map(style),
    actions: Map(actions),
    selected: true,
  }).setTimeQuantized(t);
  mutable.updateIn([COMPONENTS, component.key, KEYFRAMES], keyframes =>
    keyframes.push(keyframe.key));
  mutable.setIn([KEYFRAMES, keyframe.key], keyframe);
}

export function copyKeyframes({ mutable, keyframes }) {
  return keyframes.map(k =>
    k.set('key', incrementKey({ mutable, type: KEYFRAMES })));
}

export function initialStyleForComponent({ adState, css }) {
  const left = (adState.dimensions.get('width') - css.width) / 2;
  const top = (adState.dimensions.get('height') - css.height) / 2;

  return Map({
    ...css,
    left,
    top,
  });
}

export function unrotateDeltas({ deltaX, deltaY, rotation }) {
  const rotationAsradians = radians(rotation);
  return {
    deltaX: Math.cos(rotationAsradians) * deltaX - Math.sin(rotationAsradians) * deltaY,
    deltaY: Math.sin(rotationAsradians) * deltaX + Math.cos(rotationAsradians) * deltaY,
  };
}

export const getIncrementedComponentName = (components, type) => {
  const newIncrement = components
    .filter(component => component.type === type)
    .count() + 1;
  return `${sentenceCase(type)} ${newIncrement}`;
};

export const initializeComponent = (
  { componentType, componentInfo, components, adState, mutable },
) => {
  const style = componentType === 'group'
    ? new Map(componentInfo.css)
    : initialStyleForComponent({ adState, css: componentInfo.css });

  const newKeyframes = List([
    new KeyframeRecord({
      key: incrementKey({ mutable, type: KEYFRAMES }),
      t: -1,
      style,
    }),
  ]);

  const settings = Object.assign({ assetIds: new List() }, componentInfo.settings, {
    name: getIncrementedComponentName(components, componentType),
  });

  const componentToAdd = new ComponentRecord({
    type: componentType,
    settings: new Map(fromJS(settings)),
    locked: false,
    selected: true,
    hidden: false,
    keyframes: newKeyframes.map(k => k.key).toList(),
    uid: (new Date()).getTime().toString(36),
    key: incrementKey({ mutable, type: COMPONENTS }),
  });

  return {
    componentToAdd,
    newKeyframes,
  };
};

export const initializeAdState = ({ mutable, name, dimensions: { left, top, width, height }, style, script }) => {
  const key = incrementKey({ mutable, type: ADSTATES });
  const keyCount = mutable.getIn([ADSTATES]).count();
  const adStateName = name || `Artboard ${keyCount === 0 ? '' : keyCount + 1}`;

  const backgroundColor = style.backgroundColor || '#ffffff';
  const borderColor = style.borderColor || '#000000';
  const borderWidth = style.borderWidth || '0';

  const adStateToAdd = new AdStateRecord({
    key,
    height: 500, // for legacy section
    name: adStateName,
    selected: true,
    dimensions: new Map({ left, top, width, height }),
    viewport: new Map({ width, height }),
    backgroundColor,
    borderColor,
    borderWidth,
    script,
  });

  return { adStateToAdd };
};

export const deactivateGroup = ({ mutable, group }) => {
  mutable.setIn([COMPONENTS, group.key, 'active'], false);
};

export const deactivateGroups = ({ mutable, component = null }) => {
  const activeGroups = mutable.get(COMPONENTS).filter(c => c.isGroup() && c.isActive());
  activeGroups.forEach((group) => {
    if (component === null || !group.containsComponent(component)) {
      deactivateGroup({
        mutable,
        group,
      });
    }
  });
};

// eslint-disable-next-line max-len
export const duplicateComponent = ({ mutable, component, parent, adState, clipboardState, inGroup, assets }) => {
  const keyframes = getKeyframesForComponent({ component, clipboardState, state: mutable });

  const subComponents = getSubComponentsForComponent({
    component,
    state: clipboardState || mutable,
  });

  if (!inGroup) {
    const copiedKeyframes = copyKeyframes({ mutable, keyframes });
    const incrementedKey = incrementKey({ mutable, type: COMPONENTS });

    const copiedComponent = component.merge({
      key: incrementedKey,
      uid: incrementedKey + (new Date()).getTime().toString(36),
      keyframes: copiedKeyframes.map(k => k.key).toList(),
      hovered: false,
      subComponents: new List(),
      settings: component.settings.merge({
        assetIds: component.settings.get('assetIds').map((id) => {
          const asset = assets.find(_asset => _asset.old_id === id) || { id };
          return asset.id;
        }),
      }),
    });

    addComponent({
      mutable,
      adState,
      component: copiedComponent,
      keyframes: copiedKeyframes,
      parent,
    });

    subComponents.forEach((subComponent) => {
      duplicateComponent({
        mutable,
        parent: copiedComponent,
        component: subComponent,
        clipboardState,
        inGroup: false,
        assets,
      });
    });
  }
};

export const getReOrderedList = ({ mutable, newIndex, componentKey, componentList }) => {
  const components = mutable.get(COMPONENTS);
  const moveList = componentList.filter(key => components.getIn([key, 'selected']));
  const keepList = componentList.filter(c => !moveList.includes(c));
  const indexOffset = newIndex < componentList.indexOf(componentKey) ? 1 : 0;

  const insertBeforeKey = componentList.find((c, index) =>
    (index > (newIndex - indexOffset)) && !moveList.includes(c));

  const insertionIndex = keepList.findIndex(key => key === insertBeforeKey);

  const untilFunc = ((v, k) => k === insertionIndex);
  const prependList = keepList.takeUntil(untilFunc);
  const appendList = keepList.skipUntil(untilFunc);

  return prependList.concat(moveList).concat(appendList);
};

export const resizeBox1ToFitBox2 = ({ width1, height1, width2, height2 }) => {
  let newWidth;
  let newHeight;

  if (width2 >= width1 || (width2 <= width1 && width2 >= height2)) {
    newWidth = width1;
    newHeight = (height2 / width2) * width1;
  }
  if (newHeight >= height1 || width2 <= height2) {
    newHeight = height1;
    newWidth = (width2 / height2) * newHeight;
  }

  const deltaWidth = newWidth - width1;
  const deltaHeight = newHeight - height1;
  const deltaX = (width1 - newWidth) / 2;
  const deltaY = (height1 - newHeight) / 2;

  return { newWidth, newHeight, deltaWidth, deltaHeight, deltaX, deltaY };
};

export const alignKeyframeToParent = ({ axis, alignment, keyframe, group, adState }) => {
  const widthOrHeight = axis === 'x' ? 'width' : 'height';
  const componentSize = keyframe.style.get(widthOrHeight);
  const parentSize = group ? group.getIn(['currentStyle', widthOrHeight]) : adState.getIn(['dimensions', widthOrHeight]);

  if (alignment === 'center') return (parentSize / 2) - (componentSize / 2);

  if (alignment === 'end') return parentSize - componentSize;

  // alignment === 'start'
  return 0;
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/reducers/ad/utils.js